// Блочная пагинация: вычисление offset/limit для запроса «нескольких страниц за раз»
// и вспомогательные утилиты нарезки текущей страницы из блока

export type ComputeBlockParamsInput = {
  page: number; // 1..N
  perPage: number; // >0 (UI желаемый perPage)
  rawPrefetchPages?: number; // желаемое количество страниц в одном запросе (2–5)
  maxBackendLimit?: number; // кап лимита на бэке (напр., 20)
};

export type ComputeBlockParamsResult = {
  prefetchPages: number;
  blockLimit: number;
  blockOffset: number;
  pageIndexInsideBlock: number; // 0..prefetchPages-1
};

function clampPositiveInt(n: number, min = 1) {
  if (!Number.isFinite(n)) return min;
  const x = Math.floor(n);
  return x < min ? min : x;
}

/**
 * Вычислить параметры блочного запроса так, чтобы limit не превышал maxBackendLimit.
 * Формулы совместимы с UI-пагинацией по страницам.
 */
export function computeBlockParams(input: ComputeBlockParamsInput): ComputeBlockParamsResult {
  const page = clampPositiveInt(input.page, 1);
  const perPage = clampPositiveInt(input.perPage, 1);
  const rawPrefetch = clampPositiveInt(input.rawPrefetchPages ?? 3, 1);
  const maxLimit = clampPositiveInt(input.maxBackendLimit ?? Number.MAX_SAFE_INTEGER, 1);

  const perPageQuery = Math.min(perPage, maxLimit);
  const maxPagesByCap = Math.max(1, Math.floor(maxLimit / perPageQuery) || 1);
  const prefetchPages = Math.max(1, Math.min(rawPrefetch, maxPagesByCap));

  const blockSize = perPageQuery * prefetchPages; // <= maxLimit
  const blockIndex = Math.floor((page - 1) / prefetchPages);
  const blockLimit = blockSize;
  const blockOffset = blockIndex * blockLimit;

  const pageIndexInsideBlock = (page - 1) % prefetchPages; // 0..prefetchPages-1

  return { prefetchPages, blockLimit, blockOffset, pageIndexInsideBlock };
}

/**
 * Нарезка текущей страницы из одного загруженного блока
 */
export function slicePageFromBlock<T>(block: T[] | undefined, pageIndexInsideBlock: number, perPage: number): T[] {
  const list = Array.isArray(block) ? block : [];
  const start = pageIndexInsideBlock * clampPositiveInt(perPage, 1);
  const end = start + clampPositiveInt(perPage, 1);
  return list.slice(start, end);
}

export default computeBlockParams;
